import React, { useState } from "react";
import { Severity } from "@velaro/velaro-component-lib";
import useApi from "@/hooks/useApi";
import useToastAlert from "@/hooks/useToastAlert";
import { SectionCard, FormRow, PrimaryButton, GhostButton } from "@/components/ds";
import { ChevronDown, ChevronUp } from "@/components/ds/icons";

interface Props {
  teamId: number;
  /** Called after a config row is created (so the parent can refresh its list). */
  onConnected?: () => void;
}

/**
 * Customer-facing "Connect with OAuth 2.0" card.
 *
 * Mirrors the existing OAuth 1.0a connect pattern (button → redirect to NetSuite →
 * callback page exchanges the code). Backward support for NetSuite's 2027.1 cutoff on
 * CREATING new Token-Based Auth (OAuth 1.0a) integrations — existing OAuth 1.0a
 * connections keep working untouched.
 *
 * Flow:
 *   1. POST NetSuite/Auth/SaveCredentials → creates a connection row holding the
 *      OAuth 2.0 client id/secret (no OAuth1 token), returns its id.
 *   2. GET  NetSuite/OAuth2/AuthUrl/{id}?redirectUri=… → NetSuite consent URL; redirect.
 *   3. NetSuite redirects back to /netsuite/oauth2callback?code&state → that page
 *      POSTs NetSuite/OAuth2/Exchange and the connection flips to AuthType=oauth2.
 */
export default function NetSuiteOAuth2Connect({ teamId, onConnected }: Props) {
  const api = useApi();
  const toast = useToastAlert();

  const [displayName, setDisplayName] = useState("");
  const [accountId, setAccountId] = useState("");
  const [clientId, setClientId] = useState("");
  const [clientSecret, setClientSecret] = useState("");
  const [connecting, setConnecting] = useState(false);
  const [showHelp, setShowHelp] = useState(false);

  // NetSuite redirects the browser back to this SPA page. It must EXACTLY match the
  // Redirect URI registered on the NetSuite OAuth 2.0 integration record.
  const redirectUri = `${window.location.origin}/netsuite/oauth2callback`;

  async function connect() {
    if (!accountId.trim() || !clientId.trim() || !clientSecret.trim()) {
      toast.displayToast(Severity.Error, "Account ID, Client ID and Client Secret are required.");
      return;
    }

    setConnecting(true);
    try {
      // 1. Save the connection (client id/secret live in ConsumerKey/ConsumerSecret; no OAuth1 token).
      const saveResp = await api.messaging.post("NetSuite/Auth/SaveCredentials", {
        displayName: displayName.trim() || `NetSuite ${accountId.trim()}`,
        company: accountId.trim(),
        consumerKey: clientId.trim(),
        consumerSecret: clientSecret.trim(),
        teamId
      });
      if (!saveResp.ok) throw new Error(`Saving the connection failed (HTTP ${saveResp.status}).`);
      const { id } = await saveResp.json();

      onConnected?.();

      // Stash the redirect URI — the callback page only receives code + state from NetSuite.
      sessionStorage.setItem("nsOAuth2RedirectUri", redirectUri);

      // 2. Fetch the NetSuite consent URL and redirect the browser to it.
      const authResp = await api.messaging.get(
        `NetSuite/OAuth2/AuthUrl/${id}?redirectUri=${encodeURIComponent(redirectUri)}`
      );
      if (!authResp.ok) throw new Error(`Could not build the authorization URL (HTTP ${authResp.status}).`);
      const { url } = await authResp.json();
      window.location.href = url;
    } catch (err) {
      toast.displayToast(
        Severity.Error,
        `OAuth 2.0 connect failed: ${err instanceof Error ? err.message : String(err)}`
      );
      setConnecting(false);
    }
  }

  return (
    <SectionCard
      title="Connect with OAuth 2.0"
      description="Recommended for new connections. NetSuite is retiring the creation of Token-Based Auth (OAuth 1.0a) integrations in 2027.1 — existing connections keep working."
      className="mb-4"
      action={
        <PrimaryButton onClick={connect} disabled={connecting}>
          {connecting ? "Connecting…" : "Connect with OAuth 2.0"}
        </PrimaryButton>
      }
    >
      <div className="space-y-1">
        <FormRow label="Connection name" hint="A label for this connection (optional).">
          <input
            type="text"
            className="w-full text-sm border border-zinc-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)]"
            value={displayName}
            placeholder="e.g. Production NetSuite"
            onChange={(e) => setDisplayName(e.target.value)}
          />
        </FormRow>
        <FormRow label="Account ID" hint="Setup → Company → Company Information → Account ID (e.g. 1234567 or 1234567_SB1).">
          <input
            type="text"
            className="w-full text-sm border border-zinc-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)]"
            value={accountId}
            placeholder="1234567"
            onChange={(e) => setAccountId(e.target.value)}
          />
        </FormRow>
        <FormRow label="Client ID" hint="The integration record's Client ID (Consumer Key).">
          <input
            type="text"
            className="w-full text-sm border border-zinc-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)]"
            value={clientId}
            placeholder="Client ID"
            onChange={(e) => setClientId(e.target.value)}
          />
        </FormRow>
        <FormRow label="Client Secret" hint="The integration record's Client Secret (Consumer Secret). Shown once by NetSuite.">
          <input
            type="password"
            className="w-full text-sm border border-zinc-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)]"
            value={clientSecret}
            placeholder="Client Secret"
            onChange={(e) => setClientSecret(e.target.value)}
          />
        </FormRow>
      </div>

      <div className="mt-3">
        <GhostButton onClick={() => setShowHelp((v) => !v)}>
          <span className="inline-flex items-center gap-1">
            How do I get these values?
            {showHelp ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </span>
        </GhostButton>
      </div>

      {showHelp && (
        <div className="mt-2 rounded-lg bg-zinc-50 px-4 py-3 text-xs text-zinc-700 space-y-2">
          <p className="font-medium text-zinc-800">In NetSuite (one-time setup, as an administrator):</p>
          <ol className="list-decimal pl-5 space-y-1.5">
            <li>
              <strong>Enable the features.</strong> Setup → Company → Enable Features → SuiteCloud tab.
              Check <em>REST Web Services</em> and <em>OAuth 2.0</em>. Save.
            </li>
            <li>
              <strong>Create the integration record.</strong> Setup → Integration → Manage Integrations → New.
              Name it “Velaro”. Under Authentication, check <em>OAuth 2.0</em> and the scope
              <em> REST Web Services</em>. Uncheck Token-Based Authentication.
            </li>
            <li>
              <strong>Set the Redirect URI</strong> to exactly:
              <code className="block mt-1 px-2 py-1 bg-white border border-zinc-200 rounded break-all select-all">{redirectUri}</code>
            </li>
            <li>
              <strong>Save.</strong> NetSuite shows the <em>Client ID</em> and <em>Client Secret</em> once —
              copy both into the fields above. (If you miss them, reset the credentials on the record.)
            </li>
            <li>
              <strong>Account ID:</strong> Setup → Company → Company Information → Account ID.
            </li>
          </ol>
          <p className="text-zinc-500">
            Click <strong>Connect with OAuth 2.0</strong> — you’ll be sent to NetSuite to approve, then returned here.
            No tokens to copy by hand; Velaro stores and refreshes them automatically.
          </p>
        </div>
      )}
    </SectionCard>
  );
}
